import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import { VirtualizedTable } from "./VirtualizedTable";
import { CircularProgress } from "src/components/shared/LoadingAnimations/CircularProgress";

export default function FilePreview({ file, width = "100%", height = "100%", allowOfficeOnline = false }) {
  const [state, setState] = useState({
    text: null,
    html: null,
    table: null,
    loading: false,
    error: null,
  });

  const containerStyle = useMemo(
    () => ({
      width,
      height,
      borderRadius: 5,
      background: "#f8fafc",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    }),
    [width, height],
  );

  const ext = (file?.extension || "").toLowerCase();
  const mime = (file?.type || "").toLowerCase();

  const isImage = mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext);
  const isPDF = mime === "application/pdf" || ext === "pdf";
  const isTextLike = mime.startsWith("text/") || ["txt", "md", "csv", "json", "xml"].includes(ext);
  const isCSV = ext === "csv" || mime === "text/csv";
  const isDocx = ext === "docx" || mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  const isXlsx = ext === "xlsx" || mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const isVideo = mime.startsWith("video/");
  const isAudio = mime.startsWith("audio/");

  const isBusy = (loading, hasContent) => loading || (!hasContent && !state.error);

  useEffect(() => {
    setState({ text: null, html: null, table: null, loading: false, error: null });
  }, [file?.url, file?.blob]);

  const getText = async () => {
    if (file?.blob && typeof file.blob.text === "function") {
      return file.blob.text();
    }
    if (file?.url) {
      const res = await fetch(file.url);
      if (!res.ok) throw new Error(`Failed to load text (${res.status})`);
      return res.text();
    }
    throw new Error("No blob or url available for text");
  };

  const getArrayBuffer = async () => {
    if (file?.blob && typeof file.blob.arrayBuffer === "function") {
      return file.blob.arrayBuffer();
    }
    if (file?.url) {
      const res = await fetch(file.url);
      if (!res.ok) throw new Error(`Failed to load binary (${res.status})`);
      return res.arrayBuffer();
    }
    throw new Error("No blob or url available for binary");
  };

  useEffect(() => {
    let cancelled = false;
    if (!file) return;

    const load = async () => {
      try {
        // TEXT / CSV / JSON
        if (isTextLike) {
          setState((s) => ({ ...s, loading: true, error: null }));
          const t = await getText();
          if (cancelled) return;

          if (ext === "json") {
            try {
              const pretty = JSON.stringify(JSON.parse(t), null, 2);
              setState({ text: pretty, html: null, table: null, loading: false, error: null });
              return;
            } catch {}
          }

          if (isCSV) {
            try {
              const mod = await import("papaparse");
              const Papa = mod?.default || mod;
              const result = Papa.parse(String(t).trim(), { skipEmptyLines: true });
              const rows = result?.data || [];
              const headers = rows[0] ? rows[0] : [];
              const dataRows = rows[0] ? rows.slice(1) : rows;
              if (cancelled) return;
              setState({ text: null, html: null, table: { headers, rows: dataRows }, loading: false, error: null });
            } catch (e) {
              setState({
                text: t,
                html: null,
                table: null,
                loading: false,
                error: "CSV preview requires 'papaparse'. Install it or enable Office/Sheets preview.",
              });
            }
          } else {
            setState({ text: t, html: null, table: null, loading: false, error: null });
          }
          return;
        }

        // DOCX → HTML via mammoth
        if (isDocx) {
          setState((s) => ({ ...s, loading: true, error: null }));
          try {
            const buf = await getArrayBuffer();
            let mammothMod = null;
            try {
              mammothMod = await import("mammoth/mammoth.browser");
            } catch {
              mammothMod = await import("mammoth");
            }
            if (cancelled) return;
            const { value: html } = await mammothMod.convertToHtml({ arrayBuffer: buf });
            if (cancelled) return;
            setState({ text: null, html, table: null, loading: false, error: null });
            return;
          } catch (e) {
            if (allowOfficeOnline && file?.publicUrl) {
              setState({ text: null, html: null, table: null, loading: false, error: null });
              return;
            }
            throw new Error("DOCX preview failed. Install 'mammoth' or provide publicUrl + allowOfficeOnline.");
          }
        }

        if (isXlsx) {
          setState((s) => ({ ...s, loading: true, error: null }));
          try {
            const buf = await getArrayBuffer();
            const mod = await import("xlsx");
            const XLSX = mod?.default || mod;
            if (cancelled) return;
            const wb = XLSX.read(buf, { type: "array" });
            const first = wb.SheetNames[0];
            const sheet = wb.Sheets[first];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const headers = json[0] || [];
            const rows = json[0] ? json.slice(1) : json;
            setState({ text: null, html: null, table: { headers, rows }, loading: false, error: null });
            return;
          } catch (e) {
            if (allowOfficeOnline && file?.publicUrl) {
              setState({ text: null, html: null, table: null, loading: false, error: null });
              return;
            }
            throw new Error("XLSX preview requires 'xlsx' or Office Online fallback.");
          }
        }
      } catch (err) {
        if (cancelled) return;
        setState({ text: null, html: null, table: null, loading: false, error: err?.message || "Preview failed" });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, file?.url, file?.blob, file?.publicUrl, isTextLike, isCSV, isDocx, isXlsx, allowOfficeOnline, ext]);

  const Toolbar = () => (
    <div className="absolute -top-2 right-0 z-[1] flex -translate-y-full gap-2">
      {file?.url && (
        <a
          href={file.url}
          download={file?.name || "download"}
          className="flex items-center gap-x-1 rounded-md bg-white/80 px-2 py-1 text-xs font-medium text-gray-700 shadow ring-1 ring-gray-200 hover:bg-white">
          <ArrowDownTrayIcon className="h-3 w-3 stroke-2" />
          Download
        </a>
      )}
      {file?.url && (
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-x-1 rounded-md bg-white/80 px-2 py-1 text-xs font-medium text-gray-700 shadow ring-1 ring-gray-200 hover:bg-white">
          Open in new tab
          <ArrowTopRightOnSquareIcon className="h-3 w-3 stroke-2" />
        </a>
      )}
    </div>
  );

  const ErrorBlock = ({ message }) => (
    <div className="flex h-full w-full flex-col items-center justify-center p-6 text-center">
      <div className="mb-2 text-sm font-semibold text-red-600">Preview Error</div>
      <div className="text-xs text-gray-600">{message}</div>
    </div>
  );

  // IMAGES
  if (isImage) {
    return (
      <div className="relative z-0 w-full">
        <Toolbar />
        <div
          style={containerStyle}
          className="group">
          <img
            src={file?.url}
            alt={file?.name || "image"}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    );
  }

  // PDF
  if (isPDF) {
    return (
      <div className="relative z-0 w-full">
        <Toolbar />
        <div
          style={containerStyle}
          className="group">
          <iframe
            title="pdf"
            src={file?.url}
            className="h-full w-full"
          />
        </div>
      </div>
    );
  }

  // DOCX (always render a DOCX container; show loader until ready)
  if (isDocx) {
    const hasContent = Boolean(state.html) || (allowOfficeOnline && file?.publicUrl);
    return (
      <div className="relative z-0 w-full">
        <Toolbar />
        <div
          style={containerStyle}
          className="group">
          {isBusy(state.loading, hasContent) && (
            <CircularProgress
              variant="spinner"
              size={48}
            />
          )}
          {!state.loading && state.error && <ErrorBlock message={state.error} />}

          {!state.loading && !state.error && state.html && (
            <div
              className="prose h-full w-full overflow-auto p-4"
              dangerouslySetInnerHTML={{ __html: state.html }}
            />
          )}

          {!state.loading && !state.error && !state.html && allowOfficeOnline && file?.publicUrl && (
            <iframe
              title="office-docx"
              className="h-full w-full"
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.publicUrl)}`}
            />
          )}
        </div>
      </div>
    );
  }

  if (isXlsx) {
    const hasContent = Boolean(state.table);
    return (
      <div className="relative z-0 w-full">
        <Toolbar />
        <div
          style={containerStyle}
          className="group">
          {isBusy(state.loading, hasContent) && (
            <CircularProgress
              variant="spinner"
              size={48}
            />
          )}
          {!state.loading && state.error && <ErrorBlock message={state.error} />}

          {!state.loading && !state.error && state.table && (
            <VirtualizedTable
              headers={state.table.headers}
              rows={state.table.rows}
              height={500}
            />
          )}
        </div>
      </div>
    );
  }

  if (isTextLike) {
    const hasContent = Boolean(state.text) || Boolean(state.table);
    return (
      <div className="relative z-0 w-full">
        <Toolbar />
        <div
          style={containerStyle}
          className="group">
          {isBusy(state.loading, hasContent) && (
            <CircularProgress
              variant="spinner"
              size={48}
            />
          )}
          {!state.loading && state.error && <ErrorBlock message={state.error} />}

          {!state.loading && !state.error && state.table && (
            <VirtualizedTable
              headers={state.table.headers}
              rows={state.table.rows}
              height={500}
            />
          )}

          {!state.loading && !state.error && state.text && <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-words p-3 text-xs text-gray-800">{state.text}</pre>}
        </div>
      </div>
    );
  }

  // AUDIO
  if (isAudio) {
    return (
      <div className="relative z-0 w-full">
        <Toolbar />
        <div
          style={containerStyle}
          className="group">
          <audio
            src={file?.url}
            controls
            className="w-[90%]"
          />
        </div>
      </div>
    );
  }

  // VIDEO
  if (isVideo) {
    return (
      <div className="relative z-0 w-full">
        <Toolbar />
        <div
          style={containerStyle}
          className="group">
          <video
            src={file?.url}
            controls
            className="max-h-full max-w-full"
          />
        </div>
      </div>
    );
  }

  // Office Online fallback for other office types
  const isOfficeExt = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext);
  if (allowOfficeOnline && isOfficeExt && file?.publicUrl) {
    return (
      <div className="relative z-0 w-full">
        <Toolbar />
        <div
          style={containerStyle}
          className="group">
          <iframe
            title="office-online"
            className="h-full w-full"
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.publicUrl)}`}
          />
        </div>
      </div>
    );
  }

  // FINAL FALLBACK (only for truly unsupported/unknown)
  return (
    <div className="relative z-0 w-full">
      <Toolbar />
      <div
        style={containerStyle}
        className="group">
        <div className="text-center text-gray-600">
          <div className="mx-auto mb-2 flex h-14 w-12 items-center justify-center rounded bg-gray-200 text-[10px] font-bold">{(ext || "FILE").toUpperCase()}</div>
          <div className="text-xs">{file?.name || "Unknown file"}</div>
          {file?.url && (
            <div className="mt-2">
              <a
                href={file.url}
                download={file?.name || "download"}
                className="text-xs font-medium text-blue-600 hover:underline">
                Download
              </a>
            </div>
          )}
        </div>
        {state.error && <div className="absolute bottom-2 left-2 right-2 truncate text-center text-[11px] text-red-600">{state.error}</div>}
      </div>
    </div>
  );
}
